/**
 * Whether the user's Authelia id_token predates their rights on a project.
 *
 * ArgoCD grants a project from the `groups` claim of the id_token the proxy
 * forwards (`argoApiFetch`): `prj-<project>` read-only, `prj-<project>-editors`
 * admin (AppProject of platform-catalog's `argocd-appproject.yaml`). Creating a
 * project through the template creates those groups in LLDAP AFTER the user
 * signed in, and the id_token, refreshed or not, keeps the groups of that
 * sign-in: ArgoCD answers an empty list until the user signs in again.
 *
 * UX ONLY. The payload is decoded WITHOUT checking the signature: it only
 * decides whether to suggest signing in again, never what anyone may see or do.
 * ArgoCD verifies the token and stays the authority.
 */

import { useEffect, useState } from 'react';
import { useApi } from '@backstage/frontend-plugin-api';
import { autheliaAuthApiRef } from '../shared/auth/autheliaAuthApiRef';

/**
 * The groups ArgoCD treats as administrators, who see every project whatever
 * their `prj-…` groups.
 *
 * ASSUMPTION: hardcoded, because the frontend has no list to read —
 * `waadoo.adminGroups` is backend-only (no `@visibility frontend` in
 * `config.d.ts`), and `useIsAdmin` answers from the catalog, not from the
 * token ArgoCD reads. The two names are those of the Argo Workflows admin rule
 * transcribed in `waadoo.adminGroups`. A name missing here only shows
 * the « sign in again » alert to an administrator with an empty project; one
 * too many only hides it.
 */
export const ADMIN_TOKEN_GROUPS: readonly string[] = [
  'Administrators',
  'lldap_admin',
];

/**
 * The `groups` claim of a JWT, or undefined when the token is not a JWT, its
 * payload is not JSON, or the claim is missing or not an array of strings.
 *
 * Non-ASCII characters come out mis-decoded (`atob` yields one character per
 * byte): harmless, only ASCII group names are compared.
 */
export function idTokenGroups(idToken: string): string[] | undefined {
  const parts = idToken.split('.');
  if (parts.length !== 3) {
    return undefined;
  }
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload: unknown = JSON.parse(atob(padded));
    if (typeof payload !== 'object' || payload === null) {
      return undefined;
    }
    const groups = (payload as { groups?: unknown }).groups;
    if (
      !Array.isArray(groups) ||
      !groups.every(group => typeof group === 'string')
    ) {
      return undefined;
    }
    return groups as string[];
  } catch {
    return undefined;
  }
}

/**
 * True when the groups are known and give no access to `project`: neither
 * `prj-<project>`, nor `prj-<project>-editors`, nor an admin group. Unknown
 * groups (undefined) answer false — nothing is suggested on a guess.
 */
export function lacksProjectGroups(
  groups: string[] | undefined,
  project: string,
): boolean {
  if (!groups) {
    return false;
  }
  const granting = new Set([
    `prj-${project}`,
    `prj-${project}-editors`,
    ...ADMIN_TOKEN_GROUPS,
  ]);
  return !groups.some(group => granting.has(group));
}

/**
 * Where the signed-in user's id_token stands for `project`: `checking` while
 * it is read, `stale` when it lacks the project's groups
 * ({@link lacksProjectGroups}), `ok` otherwise — without a project, without
 * a session, when the read fails or the groups are unknown. The token is
 * read once per mount (and per project).
 */
export type ProjectAccessState = 'checking' | 'stale' | 'ok';

export function useProjectAccessState(
  project: string | undefined,
): ProjectAccessState {
  const autheliaAuth = useApi(autheliaAuthApiRef);
  // The answer and the project it is for: a new project is `checking` in
  // the very render that names it, before any effect has run.
  const [answer, setAnswer] = useState<{
    project: string;
    state: ProjectAccessState;
  }>();

  useEffect(() => {
    if (!project) {
      return undefined;
    }
    let active = true;
    const read = async () => {
      let state: ProjectAccessState = 'ok';
      try {
        // optional: true, as in argoApiFetch: never start a sign-in on mount.
        const idToken = await autheliaAuth.getIdToken({ optional: true });
        if (idToken && lacksProjectGroups(idTokenGroups(idToken), project)) {
          state = 'stale';
        }
      } catch {
        // Unknown: no suggestion. The ArgoCD read reports its own failure.
      }
      if (active) {
        setAnswer({ project, state });
      }
    };
    // `read` turns its own failure into « ok », so it cannot reject.
    void read();
    return () => {
      active = false;
    };
  }, [autheliaAuth, project]);

  if (!project) {
    return 'ok';
  }
  return answer?.project === project ? answer.state : 'checking';
}

/**
 * Whether the signed-in user's id_token lacks the groups of `project`
 * ({@link useProjectAccessState} is `stale`). False while the token is read,
 * without a project, without a session, and when the read fails.
 */
export function useStaleProjectAccess(project: string | undefined): boolean {
  return useProjectAccessState(project) === 'stale';
}
